import { useEffect, useRef, useState } from "react";
import { Layers, ArrowUpRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export const BASES = [
  { k: "dark", label: "Callejero oscuro" },
  { k: "light", label: "Callejero claro" },
  { k: "pnoa", label: "Satélite PNOA (IGN)" },
];

export const GROUPS = [
  { k: "route", label: "Recorrido" },
  { k: "cuts", label: "Cortes y tramos" },
  { k: "autocuts", label: "Cortes previos" },
  { k: "no_parking", label: "Prohibido estacionar" },
  { k: "special", label: "Zonas especiales y sentidos invertidos" },
  { k: "signs", label: "Señales, vallas y conos" },
  { k: "detours", label: "Desvíos" },
  { k: "staff", label: "Personal (Policía, P. Civil, voluntarios)" },
];

export const groupOf = (el) =>
  el.auto_generated ? "autocuts"
    : el.kind === "cut_point" || el.kind === "street_cut" ? "cuts"
      : el.kind === "special_path" || el.kind === "reversed_oneway" ? "special"
        : el.kind === "sign" ? "signs" : el.kind;

// Capas del mapa: mapa base, qué se ve (con su símbolo, que hace de leyenda) y las flechas de sentido único
export const MapLayersPanel = ({ base, setBase, hidden, setHidden, legend = {}, oneWays, setOneWays, oneWayStatus }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); window.removeEventListener("keydown", esc); };
  }, [open]);
  const toggle = (k) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="map-layers-button" title="Mapa base, capas y leyenda"
        className={`relative flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-medium border shadow-sm backdrop-blur-md transition-colors ${open ? "bg-sky-500 text-white border-sky-400" : "bg-white/95 text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
        <Layers className="w-4 h-4" /> <span className="hidden sm:inline">Capas</span>
        {(hidden.length > 0 || oneWays) && !open && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-72 max-h-[70vh] overflow-y-auto bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-3 space-y-3 shadow-2xl rise" data-testid="map-layers-panel">
          <div>
            <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 mb-1.5">MAPA BASE</div>
            <div className="grid grid-cols-3 gap-1">
              {BASES.map((b) => (
                <button key={b.k} onClick={() => setBase(b.k)} data-testid={`map-base-${b.k}`}
                  className={`px-1.5 py-1.5 rounded-lg border text-[0.6875rem] leading-tight ${base === b.k ? "border-sky-500 bg-sky-50 text-sky-800 font-semibold" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  {b.label.replace(" (IGN)", "")}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center justify-between gap-2 bg-teal-50/60 border border-teal-200 rounded-lg px-2.5 py-2 cursor-pointer">
            <span className="flex items-center gap-2 text-xs text-slate-800"><ArrowUpRight className="w-4 h-4 text-teal-600" /> Flechas de sentido único</span>
            <Switch checked={!!oneWays} onCheckedChange={setOneWays} data-testid="toggle-oneways-button" />
          </label>
          {oneWays && oneWayStatus && <div className="text-[0.625rem] text-teal-800 -mt-2" data-testid="oneways-status">{oneWayStatus}</div>}
          <div>
            <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 mb-1">QUÉ SE VE · LEYENDA</div>
            {GROUPS.map((g) => (
              <label key={g.k} className="flex items-center gap-2 py-1 text-xs text-slate-800 cursor-pointer">
                <input type="checkbox" checked={!hidden.includes(g.k)} onChange={() => toggle(g.k)} className="accent-sky-500" data-testid={`map-layer-${g.k}`} />
                <span className="w-5 flex justify-center shrink-0">{legend[g.k]}</span>{g.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
